import { Divider, ListSubheader, MenuItem, useTheme } from "@mui/material";
import { ChangeEvent } from "react";
import { SenecTextField, SenecTextFieldProps } from "src/components/inputs/input-field/SenecTextField";
import { ChevronDownIcon } from "src/foundations/icons";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";

export interface SelectionMap {
    [category: string]: SubCategory[];
}

export interface SubCategory {
    id: string;
    groupName?: string;
    name: string;
    selectionLabel?: string;
}

export interface SelectBoxProps extends SenecTextFieldProps {
    /**
     * Grouped array of entries.
     */
    data: SelectionMap;
    /**
     * Label of the material component. Will be displayed in the outline when an entry was selected.
     */
    label: string;
    /**
     * Optional. The onChange Event of the select box.
     */
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * SelectBox
 *
 * Component for a custom drop down selection field.
 * - Based on MUI TextField.
 */
export const SelectBox = (props: SelectBoxProps) => {
    const theme: SenecBaseTheme = useTheme();

    const idToLabelMap: Map<string, string> = new Map(
        Object.values(props.data)
            .flat()
            .map((entry) => [entry.id, entry.selectionLabel ?? entry.groupName ?? entry.name]),
    );

    return (
        <SenecTextField
            variant={props.variant ?? "outlined"}
            defaultValue=""
            select
            required
            {...props}
            label={props.label}
            onChange={props.onChange}
            sx={{ backgroundColor: "red" }}
            InputProps={{
                sx: {
                    width: "100%",
                    backgroundColor: theme.palette.neutralGray000.main,
                    borderRadius: "0.75rem",
                    fieldSet: { borderColor: theme.palette.neutralGray20.main },
                    ...props.sx,
                },
            }}
            SelectProps={{
                MenuProps: { sx: { maxHeight: "400px" } },
                renderValue: (id) => <>{idToLabelMap.get(id as string)}</>,
                IconComponent: (iconProps, context) => (
                    <ChevronDownIcon {...iconProps} {...context} data-testid={"select-box-dropdown-select-icon"} />
                ),
            }}
        >
            {Object.entries(props.data)
                .sort(([key], [key2]) => key.localeCompare(key2))
                .map(([key, dataElem]) => {
                    if (dataElem.length === 1) {
                        return dataElem[0].groupName ? renderTopicGroup(key, dataElem) : renderTopicMenuItem(dataElem[0]);
                    } else if (dataElem.length > 1) {
                        return renderTopicGroup(key, dataElem);
                    }
                })}
        </SenecTextField>
    );
};

const renderTopicMenuItem = (data: SubCategory) => {
    return [
        <MenuItem key={data.id} value={data.id} sx={{ p: "0.5rem 1rem" }}>
            {data.name}
        </MenuItem>,
        <Divider key={`${data.name}_divider`} sx={{ margin: "0 !important" }} />,
    ];
};

const renderTopicGroup = (groupName: string, groupData: SubCategory[]) => {
    const groupItems = groupData.map((data) => {
        return (
            <MenuItem key={data.id} value={data.id} sx={{ textIndent: "1rem", p: "0.5rem 1rem" }}>
                {data.groupName}
            </MenuItem>
        );
    });

    return [
        <ListSubheader key={groupName} sx={{ fontSize: "inherit", fontStyle: "italic" }}>
            {groupName}
        </ListSubheader>,
        groupItems,
        <Divider key={`${groupName}_divider`} sx={{ margin: "0 !important" }} />,
    ];
};
